import { IconButton, Link, Paper, ThemeProvider, createTheme } from "@mui/material"
import { DataGrid, gridStringOrNumberComparator } from "@mui/x-data-grid"
import CompliancePlip from "../../../../components/CompliancePlip"
import { TableButton } from "../../../../components/Buttons.styled"
import { useState } from "react"
import { FormattedBioBox } from "../types"
import { Edit as EditIcon } from '@mui/icons-material'
import { last } from 'remeda'
import PracticeAssignment from './PracticeAssignment'
import { addError } from "../../../../redux/alerts"
import { useAppDispatch, useAppSelector } from "../../../../redux/store"
import { useNavigate } from "react-router-dom"
import { ChildPracticeMetadata, ComplianceStatus, LoadingState } from "../../../../types"
import { toggleBioboxActivation } from "../../../../services/biologicalServices"
import { selectChildPractices, selectLoadingState } from "../../../../redux/customer"
import { useSelector } from "react-redux"

interface BiologicalInventoryTable {
  bioBoxes: FormattedBioBox[] | null
  onUpdate: (box: FormattedBioBox) => void
  updateBoxPractice: (practice: ChildPracticeMetadata, box: FormattedBioBox) => void
}

const ComplianceMessage = {
  1: 'Valid',
  2: 'Expiring',
  3: 'Expired',
  4: 'Inactive'
}

const getComplianceMessage = (biobox: FormattedBioBox) => {
  const complianceMessage = biobox.compliance.length > 1 ? last(biobox.compliance)?.message_short : biobox.compliance[0].message_short
  const complianceStatus = biobox.compliance[0].compliance_status as 1 | 2 | 3 | 4
  return ComplianceMessage[complianceStatus] !== complianceMessage
    ? `${ComplianceMessage[complianceStatus]} ${complianceMessage}`
    : complianceMessage
}

const tableTheme = createTheme({
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 13
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: "bold"
        }
      }
    }
  }
})

const badgeStyles = {
  display: 'inline-block',
  minWidth: '10px',
  padding: '3px 7px',
  color: '#fff',
  borderRadius: '10px',
  lineHeight: '1',
}

const BiologicalInventoryTable = (props: BiologicalInventoryTable) => {
  const { bioBoxes, onUpdate, updateBoxPractice } = props

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const loadingState = useAppSelector(selectLoadingState)

  const [loadingItemId, setLoadingItemId] = useState<number | null>(null)
  const [editingItemId, setEditingItemId] = useState<number | null>(null)
  const [savingPracticeItemId, setSavingPracticeItemId] = useState<number | null>(null)

  const childPractices = useSelector(selectChildPractices)

  const isLoading = !Boolean(bioBoxes) || loadingState === LoadingState.Loading

  const handleActivate = async (box: FormattedBioBox) => {
    try {
      setLoadingItemId(box.box_id as number)

      const updatedBiobox = await toggleBioboxActivation(box.customer_number, box.box_id)

      box.active = updatedBiobox.active
      box.compliance[0] = {
        compliance_code: updatedBiobox.compliance_code,
        message_long: updatedBiobox.message_long,
        message_short: updatedBiobox.message_short,
        compliance_status: updatedBiobox.active ? updatedBiobox.compliance_status : ComplianceStatus.Inactive,
      }

      onUpdate(box)
      setLoadingItemId(null)
    } catch (error) {
      console.error(error)
      setLoadingItemId(null)
      dispatch(addError('There was a problem updating the box'))
    }
  }

  const handleUpdateBoxPractice = async (practice: ChildPracticeMetadata, box: FormattedBioBox) => {
    setEditingItemId(null)
    setSavingPracticeItemId(box.box_id as number)
    await updateBoxPractice(practice, box)
    setSavingPracticeItemId(null)
  }

  const getTestsLeftStyles = (boxSize: number, testsRun: number) => {
    if (testsRun === boxSize) {
      return {
        backgroundColor: '#f05050',
        ...badgeStyles
      }
    } else if ((boxSize - testsRun) < 5) {
      return {
        backgroundColor: '#F4B122',
        ...badgeStyles
      }
    }

    return {}
  }

  const getExpirationDateStyles = (date: string) => {
    const today = new Date()
    const expDate = new Date(date)
    if (today > expDate) {
      return { color: '#f05050' }
    } else if (new Date(today.setMonth(today.getMonth() + 1)) > expDate) {
      return { color: '#F4B122' }
    }
    return {}
  }

  const handleEditClick = (boxId?: number) => {
    if (boxId) setEditingItemId(boxId)
  }

  return (
    <Paper>
      <ThemeProvider theme={tableTheme}>
        <DataGrid
          sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
          rows={(bioBoxes ?? []).map((row) => ({
            id: row.box_id,
            key: row.box_id,
            ...row
          }))}
          columns={[
            {
              field: "compliance",
              headerName: "Status",
              sortable: true,
              width: 90,
              renderCell: (params) => (
                <CompliancePlip
                  message={getComplianceMessage(params.row)}
                  status={params.row.compliance[0].compliance_status} />
              ),
              align: "center",
              getSortComparator: (sortDirection) => {
                const modifier = sortDirection === 'desc' ? -1 : 1
                return (a, b, cellParams1, cellParams2) => {
                  const aCompliance = a[0].compliance_status
                  const bCompliance = b[0].compliance_status
                  if (aCompliance === bCompliance) {
                    return 0
                  }

                  if (aCompliance === 4 || aCompliance === null) {
                    return 1
                  }

                  if (bCompliance === 4 || bCompliance === null) {
                    return -1
                  }

                  return (
                    modifier *
                    gridStringOrNumberComparator(aCompliance, bCompliance, cellParams1, cellParams2)
                  )
                }
              },
            },
            {
              field: "display_box_number",
              headerName: "Box Number",
              flex: 1,
              minWidth: 150,
            },
            {
              field: "name",
              headerName: "Name",
              flex: 1,
              minWidth: 500,
              renderCell: (params) => (
                <>
                  {
                    editingItemId === params.row.box_id ? (
                      <PracticeAssignment
                        childPractices={childPractices}
                        bioBox={params.row}
                        updateBoxPractice={handleUpdateBoxPractice}
                        onClose={() => setEditingItemId(null)}
                      />
                    ) : (
                      <span>
                        <Link component="button" onClick={() => navigate(`/corp/practice/${params.row?.customer_number}/biological`)} sx={{ display: 'inline-flex', alignItems: 'center', color: '#19a9d5', textDecoration: 'none' }}>
                          {params.row?.name?.trim()} ({params.row?.customer_number})
                        </Link>
                        <IconButton
                          disabled={savingPracticeItemId === params.row.box_id}
                          size='small'
                          sx={{ marginLeft: '10px', marginBottom: '10px' }}
                          onClick={() => handleEditClick(params.row.box_id)}
                        >
                          <EditIcon fontSize="small" sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </span>
                    )
                  }
                </>
              )
            },
            {
              field: "city",
              headerName: "Location",
              flex: 1,
              minWidth: 100,
            },
            {
              field: "tests_left",
              headerName: "Tests Left",
              flex: 1,
              minWidth: 50,
              renderCell: (params) => (
                <><span style={getTestsLeftStyles(params.row.box_size ?? 0, params.row.tests_run ?? 0)}>{params.row.tests_left}</span><em className="text-muted"> of {params.row.box_size}</em></>
              )
            },
            {
              field: "exp_date",
              headerName: "Expires",
              flex: 1,
              minWidth: 45,
              renderCell: (params) => (
                <span style={getExpirationDateStyles(params.row.exp_date ?? '')}>
                  {params.row.exp_date ? new Date(params.row.exp_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : ''}
                </span>
              )
            },
            {
              field: "deactivate",
              headerName: "",
              flex: 1,
              minWidth: 55,
              renderCell: (params) => (
                <>
                  {params.row.compliance[0].compliance_status === 4 && params.row.compliance[0].compliance_code === 'BIO_BOX_INACTIVE' && (
                    <TableButton size="small" variant="contained" onClick={() => handleActivate(params.row)} disabled={loadingItemId === params.row.id}>
                      Activate
                    </TableButton>
                  )}
                  {params.row.compliance[0].compliance_status !== 4 && (
                    <TableButton size="small" variant="contained" onClick={() => handleActivate(params.row)} disabled={loadingItemId === params.row.id}>
                      Deactivate
                    </TableButton>
                  )}
                </>
              )
            }
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
            sorting: {
              sortModel: [
                {
                  field: 'compliance',
                  sort: 'desc'
                }
              ]
            }
          }}
          pageSizeOptions={[5, 10, 20, 25, 50, 100]}
          loading={isLoading}
          autoHeight
          density="compact"
        />
      </ThemeProvider>
    </Paper >
  )
}

export default BiologicalInventoryTable
